import { CloseOutlined } from "@ant-design/icons";
import { Button, Flex, Modal, Spin, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useUserPermissions } from "~context/UserPermissionsContext";
import type { ImageBaseTypes } from "~enums/imageBaseTypes";
import type { ImageTypes } from "~enums/imageTypes";
import { type PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Image } from "~types/image";
import { ImageUploader, WarningsTooltip } from "./components";
import { useDeleteImage, useUploadImage } from "./hooks";
import { getImageQualityWarnings } from "./utilities";

import "./UploadImageListView.scss";
import { ImageView } from "~components/ImageView";
import { ImageSizes } from "~enums/imageSizes.ts";
import { findImageByTypes } from "~utilities/findImageByTypes.ts";

type Props = {
  baseType: ImageBaseTypes;
  baseId: string;
  imageType: ImageTypes;
  initialImage: Image;
  permissionKey: PermissionNames;
  images: Image[];
};

const UploadImageListView = ({ permissionKey, images, baseType, baseId, imageType }: Props) => {
  const { hasPermission } = useUserPermissions();
  const [currentImage, setCurrentImage] = useState<Image | undefined>();
  const [isImageLoading, setIsImageLoading] = useState<boolean>(!!currentImage);
  const [modal, modalContextHolder] = Modal.useModal();
  const { formatMessage } = useIntl();
  const { mutateAsync: uploadImage, isPending: isImageUploading } = useUploadImage();
  const { mutateAsync: deleteImage, isPending: isImageDeleting } = useDeleteImage();
  const warnings = useMemo<string[]>(
    () => (currentImage ? getImageQualityWarnings(currentImage, formatMessage) : []),
    [currentImage, formatMessage],
  );
  const showWarnings = currentImage && !isImageLoading && warnings.length > 0;
  const isEditable = hasPermission(permissionKey, PermissionOperations.UPDATE);
  const showDeleteIcon = isEditable && currentImage && !isImageLoading;
  const isUploadDisabled = isImageLoading || isImageUploading || isImageDeleting;

  useEffect(() => {
    const initialImage = findImageByTypes(images, [imageType]);
    if (initialImage) {
      setCurrentImage(initialImage);
    }
  }, [images, imageType]);

  const handleDelete = () =>
    modal.confirm({
      title: formatMessage({ id: "image.delete" }),
      content: formatMessage({ id: "image.delete.confirm" }),
      okText: formatMessage({ id: "delete" }),
      onOk: () => deleteImage(currentImage!.imageId).then(() => setCurrentImage(undefined)),
    });

  const handleUpload = (file) =>
    uploadImage({ baseId, baseType, file, imageType }).then((response) => {
      setIsImageLoading(true);
      setCurrentImage(response?.data?.[0]);
    });

  return (
    <>
      {modalContextHolder}
      <Flex gap={16} align="center" className="upload-image-list-view">
        <div className="image-list-view-image-container">
          {(isImageLoading || isImageUploading) && <Spin size="default" className="spinner" />}
          {currentImage && (
            <ImageView
              imageType={imageType}
              imageUrl={currentImage.url}
              size={ImageSizes.Small}
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          )}
          {showWarnings && <WarningsTooltip warnings={warnings} className="warning-tooltip" />}
          {showDeleteIcon && (
            <Tooltip title={formatMessage({ id: "delete" })} className="delete-tooltip">
              <Button size="small" type="text" icon={<CloseOutlined />} disabled={isImageDeleting} onClick={handleDelete} />
            </Tooltip>
          )}
        </div>
        {isEditable && <ImageUploader handleDrop={handleUpload} disabled={isUploadDisabled} />}
      </Flex>
    </>
  );
};

export default UploadImageListView;
